import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { publishDisplayName } from "@/hooks/useDisplayName";
import { celebrate } from "@/lib/celebrate";
import { ArrowLeft, Loader2, Upload, Lock, CheckCircle2, Circle, Info, Sparkles, Check } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AvatarCropperDialog from "@/components/profile/AvatarCropperDialog";
import { useMembership, formatExpiryDate } from "@/hooks/useMembership";
import { CalendarClock } from "lucide-react";
import { GOALS, TIMEFRAMES } from "@/data/goals";
import { AVATAR_BUCKET, avatarObjectPath, clearAvatarUrlCache, resolveAvatarUrl } from "@/lib/avatar";

const MAX_AVATAR_BYTES = 4 * 1024 * 1024;

const ProfileSchema = z.object({
  full_name: z.string().trim().min(1, "Name is required").max(80),
  headline: z.string().trim().max(120).optional().or(z.literal("")),
  bio: z.string().trim().max(600).optional().or(z.literal("")),
});

type ProfileRow = {
  id: string;
  full_name: string | null;
  headline: string | null;
  bio: string | null;
  avatar_url: string | null;
  locked_at: string | null;
  city: string | null;
  primary_goal: string | null;
  goal_timeframe: string | null;
};


export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [row, setRow] = useState<ProfileRow | null>(null);
  const [fullName, setFullName] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  // Moved off onboarding: these belong with the rest of the profile, not in the
  // learner's very first screen.
  const [city, setCity] = useState("");
  const [goal, setGoal] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<string | null>(null);

  const [avatarPath, setAvatarPath] = useState<string | null>(null);
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);

  const [cropperOpen, setCropperOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);

  // Auto-fill detection: snapshot which fields were already complete when the editor loaded
  const [prefilled, setPrefilled] = useState<Record<string, boolean>>({});

  const locked = !!row?.locked_at;

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, headline, bio, avatar_url, locked_at, city, primary_goal, goal_timeframe")
        .eq("id", user.id)
        .maybeSingle();
      if (error) toast.error("Couldn't load profile");
      const r = (data as ProfileRow) ?? null;
      setRow(r);
      const initName = r?.full_name ?? "";
      const initHeadline = r?.headline ?? "";
      const initBio = r?.bio ?? "";
      const initAvatar = r?.avatar_url ?? null;
      setFullName(initName);
      setHeadline(initHeadline);
      setBio(initBio);
      setCity(r?.city ?? "");
      setGoal(r?.primary_goal ?? null);
      setTimeframe(r?.goal_timeframe ?? null);
      setAvatarPath(initAvatar);

      // Sign at read time; never render the stored value directly.
      setAvatarSrc(await resolveAvatarUrl(initAvatar));
      setPrefilled({
        name: initName.trim().length > 0,
        avatar: !!initAvatar,
        headline: initHeadline.trim().length >= 8,
        bio: initBio.trim().length >= 40,
      });
      setLoading(false);
    })();
  }, [user]);

  // Completeness — weighted across the four fields
  const checklist = useMemo(() => {
    const hasAvatar = !!(pendingPreview || avatarPath);
    const hasName = fullName.trim().length > 0;
    const hasHeadline = headline.trim().length >= 8;
    const hasBio = bio.trim().length >= 40;
    return [
      { key: "name", label: "Full name", hint: "Any non-empty name.", done: hasName, weight: 25 },
      { key: "avatar", label: "Profile photo", hint: "A cropped avatar uploaded or pending.", done: hasAvatar, weight: 25 },
      { key: "headline", label: "Headline", hint: "At least 8 characters.", done: hasHeadline, weight: 25 },
      { key: "bio", label: "Bio", hint: "At least 40 characters.", done: hasBio, weight: 25 },
    ];
  }, [fullName, headline, bio, avatarPath, pendingPreview]);

  const completeness = checklist.reduce((s, i) => s + (i.done ? i.weight : 0), 0);
  const prefilledCount = checklist.filter((i) => i.done && prefilled[i.key]).length;

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error("Image must be under 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result as string);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropConfirm = (blob: Blob, previewUrl: string) => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingBlob(blob);
    setPendingPreview(previewUrl);
    setCropperOpen(false);
    setRawImageSrc(null);
  };

  /** Uploads the cropped avatar and returns its storage object path. */
  const uploadPendingAvatar = async (): Promise<string | null> => {
    if (!pendingBlob || !user) return null;
    setUploading(true);
    try {
      const path = avatarObjectPath(user.id, "jpg");
      const { error: upErr } = await supabase.storage
        .from(AVATAR_BUCKET)
        .upload(path, pendingBlob, { upsert: true, contentType: "image/jpeg" });
      if (upErr) throw upErr;
      return path;
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!user) return;
    const parsed = ProfileSchema.safeParse({ full_name: fullName, headline, bio });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSaving(true);
    try {
      let newAvatarPath: string | null = null;
      if (pendingBlob) {
        newAvatarPath = await uploadPendingAvatar();
      }
      const payload: {
        headline: string | null;
        bio: string | null;
        city: string | null;
        primary_goal: string | null;
        goal_timeframe: string | null;
        full_name?: string;
        avatar_url?: string;
      } = {
        headline: parsed.data.headline || null,
        bio: parsed.data.bio || null,
        city: city.trim() || null,
        primary_goal: goal,
        goal_timeframe: timeframe,
      };
      if (!locked) payload.full_name = parsed.data.full_name;
      // The path, not a signed URL — signed URLs expire and would break the avatar.
      if (newAvatarPath) payload.avatar_url = newAvatarPath;


      const { error } = await supabase.from("profiles").update(payload).eq("id", user.id);
      if (error) throw error;
      if (newAvatarPath) {
        if (pendingPreview) URL.revokeObjectURL(pendingPreview);
        clearAvatarUrlCache(newAvatarPath);
        setAvatarPath(newAvatarPath);
        setAvatarSrc(await resolveAvatarUrl(newAvatarPath));
        setPendingBlob(null);
        setPendingPreview(null);
      }
      // The name the learner just chose is the name the whole app uses.
      publishDisplayName((payload.full_name ?? fullName).trim() || null);
      celebrate("small");
      toast.success("Profile saved — taking you to your dashboard");
      window.setTimeout(() => navigate("/dashboard"), 700);
    } catch (err: any) {
      toast.error(err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const initials = (fullName || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const displayAvatar = pendingPreview || avatarSrc;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto flex items-center gap-3 px-4 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-xl font-semibold">My Profile</h1>
          <div className="ml-auto">
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings">Account settings</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-2xl px-4 py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            {/* Completeness card */}
            <Card>
              <CardContent className="space-y-4 p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p id="completeness-label" className="text-sm text-muted-foreground">
                        Profile completeness
                      </p>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="How is profile completeness calculated"
                              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-xs" role="tooltip">
                            <p className="mb-2 text-xs font-medium">How this score is calculated</p>
                            <ul className="space-y-1 text-xs">
                              {checklist.map((i) => (
                                <li key={i.key} className="flex items-start justify-between gap-3">
                                  <span>
                                    <span className="font-medium">{i.label}</span>
                                    <span className="text-muted-foreground"> — {i.hint}</span>
                                  </span>
                                  <span className="shrink-0 tabular-nums text-muted-foreground">
                                    +{i.weight}%
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="font-display text-2xl font-semibold" aria-hidden="true">
                      {completeness}%
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {completeness === 100 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Complete
                      </span>
                    )}
                    {prefilledCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[11px] text-muted-foreground">
                        <Sparkles className="h-3 w-3" aria-hidden="true" /> {prefilledCount} pre-filled
                      </span>
                    )}
                  </div>
                </div>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-labelledby="completeness-label"
                        aria-describedby="completeness-breakdown"
                        className="block w-full cursor-help rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <Progress
                          value={completeness}
                          className="h-2"
                          aria-label={`Profile completeness ${completeness} percent`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs" role="tooltip">
                      <ul className="space-y-1 text-xs">
                        {checklist.map((i) => (
                          <li key={i.key} className="flex items-center justify-between gap-3">
                            <span className="flex items-center gap-1.5">
                              {i.done ? (
                                <CheckCircle2 className="h-3 w-3 text-primary" aria-hidden="true" />
                              ) : (
                                <Circle className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                              )}
                              {i.label}
                            </span>
                            <span className="tabular-nums text-muted-foreground">
                              {i.done ? `+${i.weight}%` : `0 / ${i.weight}%`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {/* Screen-reader-only breakdown — always announced, no hover required */}
                <p id="completeness-breakdown" className="sr-only">
                  {`${completeness}% complete. `}
                  {checklist
                    .map((i) =>
                      i.done
                        ? `${i.label} done, ${i.weight} percent earned`
                        : `${i.label} missing, ${i.weight} percent available`,
                    )
                    .join(". ")}
                  .
                </p>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {checklist.map((item) => (
                    <li key={item.key} className="flex items-center gap-2 text-sm">
                      {item.done ? (
                        <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      )}
                      <span className={item.done ? "text-foreground" : "text-muted-foreground"}>
                        {item.label}
                      </span>
                      <span className="sr-only">
                        {item.done ? "complete" : "not yet complete"}
                        {item.done && prefilled[item.key] ? ", pre-filled from your saved profile" : ""}
                      </span>
                      {item.done && prefilled[item.key] && (
                        <span
                          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"
                          aria-hidden="true"
                        >
                          <Sparkles className="h-3 w-3" /> pre-filled
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Account details — email, joined, membership */}
            <Card>
              <CardContent className="space-y-3 p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Account
                </p>
                <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div>
                    <dt className="text-xs text-muted-foreground">Email</dt>
                    <dd className="font-medium break-all">{user?.email ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Joined</dt>
                    <dd className="font-medium">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">User ID</dt>
                    <dd className="font-mono text-xs text-muted-foreground truncate">{user?.id?.slice(0, 8) ?? "—"}…</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Status</dt>
                    <dd className="font-medium">{row?.locked_at ? "Verified" : "Active"}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <AssessmentExpiryCard />


            <Card>
              <CardContent className="space-y-8 p-6 md:p-8">
                {/* Avatar */}
                <section className="flex items-center gap-5">
                  <Avatar className="h-20 w-20">
                    {displayAvatar ? <AvatarImage src={displayAvatar} alt="Your avatar" /> : null}
                    <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFilePicked}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading || saving}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {displayAvatar ? "Change photo" : "Upload photo"}
                      </Button>
                      {pendingPreview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            URL.revokeObjectURL(pendingPreview);
                            setPendingPreview(null);
                            setPendingBlob(null);
                          }}
                        >
                          Discard
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {pendingPreview
                        ? "Preview — saved when you click Save changes."
                        : "PNG or JPG, up to 4 MB. You'll crop it next."}
                    </p>
                  </div>
                </section>

                {/* Name */}
                <section className="space-y-2">
                  <Label htmlFor="full_name" className="flex items-center gap-2">
                    Full name
                    {locked && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <Input
                    id="full_name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    maxLength={80}
                    disabled={locked}
                    placeholder="Your name"
                  />
                  {locked && (
                    <p className="text-xs text-muted-foreground">
                      Name is locked after your first assessment. Contact support to change it.
                    </p>
                  )}
                </section>

                {/* Headline */}
                <section className="space-y-2">
                  <Label htmlFor="headline">Headline</Label>
                  <Input
                    id="headline"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    maxLength={120}
                    placeholder="e.g. Aspiring product designer · Class 12"
                  />
                  <p className="text-xs text-muted-foreground">{headline.length}/120</p>
                </section>

                {/* Bio */}
                <section className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    maxLength={600}
                    rows={5}
                    placeholder="Tell people what you're exploring, what you care about, and what you're learning."
                  />
                  <p className="text-xs text-muted-foreground">{bio.length}/600</p>
                </section>

                {/* City — moved here from onboarding */}
                <section className="space-y-2">
                  <Label htmlFor="city">Where you're based</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    maxLength={80}
                    placeholder="City"
                  />
                  <p className="text-xs text-muted-foreground">
                    Helps us surface pathways and opportunities near you.
                  </p>
                </section>

                {/* Your quest — the goal captured at onboarding, changeable any time */}
                <section className="space-y-4 rounded-3xl border border-border bg-muted/20 p-5">
                  <div>
                    <h2 className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" /> Your quest
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                      What you're aiming for, and by when. Change it whenever your plans shift.
                    </p>
                  </div>

                  <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-muted-foreground mb-1">
                      What would make this worth it for you?
                    </legend>
                    <div className="grid gap-2">
                      {GOALS.map((g) => {
                        const active = goal === g.id;
                        return (
                          <button
                            key={g.id}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setGoal(g.id)}
                            className={`flex min-h-[48px] w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                              active
                                ? "border-primary bg-primary/10"
                                : "border-border bg-card/60 hover:border-primary/40"
                            }`}
                          >
                            <span className="text-lg" aria-hidden="true">{g.emoji}</span>
                            <span className="flex-1 text-sm text-foreground">{g.label}</span>
                            {active && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-muted-foreground mb-1">
                      When does this matter?
                    </legend>
                    <div className="flex flex-wrap gap-2">
                      {TIMEFRAMES.map((t) => {
                        const active = timeframe === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setTimeframe(t.id)}
                            className={`min-h-[44px] rounded-full border px-4 text-sm transition-colors ${
                              active
                                ? "border-primary bg-primary/10 text-foreground"
                                : "border-border bg-card/60 text-muted-foreground hover:border-primary/40"
                            }`}
                          >
                            <span className="mr-1.5" aria-hidden="true">{t.emoji}</span>
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                </section>



                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>
                    Cancel
                  </Button>
                  <Button onClick={onSave} disabled={saving || uploading}>
                    {(saving || uploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <AvatarCropperDialog
        open={cropperOpen}
        imageSrc={rawImageSrc}
        onCancel={() => {
          setCropperOpen(false);
          setRawImageSrc(null);
        }}
        onConfirm={onCropConfirm}
      />
    </div>
  );
}

function AssessmentExpiryCard() {
  const m = useMembership();
  if (m.loading) return null;
  if (!m.hasMembership) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-5">
          <CalendarClock className="h-5 w-5 text-muted-foreground" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium text-foreground">
              Assessment access
            </p>
            <p className="text-xs text-muted-foreground">
              You haven't activated WCC360 yet. Each purchase gives you 12 months of full results access.
            </p>
          </div>
          <Button size="sm" asChild>
            <Link to="/payment">Get access</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const expiringSoon =
    m.daysUntilExpiry !== null && m.daysUntilExpiry <= 60 && !m.isExpired;

  return (
    <Card
      className={
        m.isExpired
          ? "border-amber-500/40 bg-amber-500/5"
          : expiringSoon
          ? "border-amber-500/40 bg-amber-500/5"
          : ""
      }
    >
      <CardContent className="flex flex-wrap items-center gap-3 p-5">
        <CalendarClock
          className={`h-5 w-5 ${
            m.isExpired || expiringSoon ? "text-amber-500" : "text-primary"
          }`}
        />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-foreground">
            WCC360 results access
          </p>
          <p className="text-xs text-muted-foreground">
            {m.isExpired ? (
              <>Your access ended on {formatExpiryDate(m.expiresAt)}. Renew to view your full report.</>
            ) : (
              <>
                Valid until{" "}
                <span className="font-semibold text-foreground">
                  {formatExpiryDate(m.expiresAt)}
                </span>
                {m.daysUntilExpiry !== null && (
                  <> · {m.daysUntilExpiry} day{m.daysUntilExpiry === 1 ? "" : "s"} remaining</>
                )}
              </>
            )}
          </p>
        </div>
        {(m.isExpired || expiringSoon) && (
          <Button size="sm" asChild>
            <Link to="/payment">
              {m.isExpired ? "Renew access" : "Renew early"}
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

